/**
 * Mixpanel, restored after the React port.
 *
 * The Jinja templates carried the whole integration (header.html /
 * base.html / login.html); porting the pages off Jinja silently dropped
 * every event. This module is the SPA's single seam to Mixpanel — same
 * tokens, same event names, same identify shape as the templates, so the
 * existing Mixpanel dashboards keep reading without a migration.
 *
 * The tokens are Mixpanel CLIENT tokens — public by design (they ship in
 * every page's source) and already committed in app/templates/header.html.
 *
 * Attribution under impersonation follows the old app: events belong to
 * the person really signed in (Flask-Login's current_user was always the
 * actor), never to the impersonation target — a support session must not
 * write events into a customer's analytics identity.
 *
 * Two leaks closed on 2026-10-21 (Mixpanel review of prod data):
 *  - `impersonating` is a super property, which Mixpanel persists in
 *    localStorage. It was registered when a "view as" began and never
 *    removed, so a staff member's every later event carried a customer's
 *    email. It is now unregistered whenever the session is not an
 *    impersonation, and the whole identity is reset on sign-out.
 *  - A session that says it impersonates but whose real sign-in IS the
 *    target (a browser signed in as the customer while an impersonation
 *    was still recorded, prod 2026-10-16) cannot be attributed honestly:
 *    the staff member is unknown and the customer's identity is the only
 *    one on offer. Nothing is tracked in that state.
 */
import mixpanel from 'mixpanel-browser'

import type { Session } from './api'
import { clearViewAsOnSignOut } from './viewAs'

const PROD_TOKEN = 'e536492a47257a3e056677d1f44e7876'
const DEV_TOKEN = '13724d584d8f997d7435f932103ae479'

let ready = false
// True while the session is an impersonation that cannot be attributed to
// the staff member (see the header). trackEvent drops everything.
let suppressed = false

/** Same rule as the Jinja block: only the production hostname reports to
 *  the production project; localhost, dev and preview all go to dev. */
export function isProdAnalyticsHost(hostname: string): boolean {
  return hostname === 'app.priorisports.com'
}

export function initAnalytics(): void {
  try {
    const isProd = isProdAnalyticsHost(window.location.hostname)
    mixpanel.init(isProd ? PROD_TOKEN : DEV_TOKEN, {
      // The SPA owns Page View itself (trackPageView + PageViewTracker);
      // the SDK's automatic pageview only fires on full document loads
      // and would double-count the first one.
      track_pageview: false,
      persistence: 'localStorage',
      ip: true,
      secure_cookie: isProd,
      cross_subdomain_cookie: false,
    })
    mixpanel.register({ environment: isProd ? 'production' : 'development' })
    ready = true
  } catch (e) {
    console.error('[Mixpanel] init error:', e)
  }
}

export function identifyUser(session: Session): void {
  if (!ready) return
  try {
    const target = session.impersonating
    const actor = session.actor
    if (target && (!actor || actor.id === target.id)) {
      // Claims an impersonation but the only identity on offer is the
      // customer's: tracking would write staff activity onto their profile.
      suppressed = true
      return
    }
    suppressed = false
    // The real sign-in, never the impersonation target.
    const who = target && actor ? actor : session.user
    mixpanel.identify(who.email)
    mixpanel.people.set({
      $email: who.email,
      $name: who.email.split('@')[0],
      user_id: who.id,
    })
    mixpanel.register({
      user_id: who.id,
      user_email: who.email,
      // The org whose data is on screen (the target's, when viewing as).
      org_id: session.org?.id ?? null,
      org_name: session.org?.name ?? null,
      ...(target
        ? { impersonating: target.email, is_impersonation: true }
        : { is_admin: session.user.is_admin }),
    })
    if (!target) {
      // Persisted by an earlier "view as"; it must not outlive it.
      mixpanel.unregister('impersonating')
      mixpanel.unregister('is_impersonation')
    }
  } catch (e) {
    console.error('[Mixpanel] identify error:', e)
  }
}

/** Forget the signed-in identity and every super property — call before
 *  leaving for /auth/logout, so the next person on this browser starts
 *  anonymous instead of inheriting the last one's properties. */
export function resetAnalytics(): void {
  suppressed = false
  if (!ready) return
  try {
    mixpanel.reset()
  } catch (e) {
    console.error('[Mixpanel] reset error:', e)
  }
}

/** Sign out: reset analytics, drop this tab's view-as (the server's
 *  logout revokes every grant; SDET review of #931, 76.5), then the real
 *  navigation /auth/logout needs. Every sign-out comes through here. */
export function signOut(logoutUrl: string): void {
  resetAnalytics()
  clearViewAsOnSignOut()
  window.location.assign(logoutUrl)
}

const SURFACES: [RegExp, string][] = [
  // /match-room since 2026-10-23; the old address still matches so an event
  // fired mid-redirect lands on the same surface. The NAME stays match_video:
  // renaming the surface would split every Mixpanel series at the rename date.
  [/^\/(match-room|match-video)\//, 'match_video'],
  [/^\/(attacking|defending)-match-summary\//, 'match_summary'],
  // /clip-room since 2026-10-23; the old address still matches mid-redirect.
  [/^\/(clip-room|clip-lab)\//, 'clip_lab'],
  [/^\/(insights-lab|analytics-collaboration)(\/|$)/, 'insights_lab'],
  // /analysis-room since 2026-10-21; the old address still matches
  // mid-redirect, and the NAME stays workshop for the same reason as above.
  [/^\/(analysis-room|insights-workshop)(\/|$)/, 'workshop'],
  [/^\/insights-packs(\/|$)/, 'insight_pack'],
  [/^\/upload-match(\/|$)/, 'upload'],
  [/^\/(developer-tools|api-tokens)(\/|$)/, 'developer_tools'],
  [/^\/dashboard(\/|$)/, 'dashboard'],
  [/^\/(match-room|matches|games)(\/|$)/, 'matches'],
  [/^\/playlists(\/|$)/, 'playlists'],
  [/^\/notebooks(\/|$)/, 'notebooks'],
  [/^\/(manage-|ops|labeling|activity|lead-links|database-info)/, 'admin'],
]

// Routes whose path carries the game: /match-video/<code>, /clip-lab/<code>,
// /insights-packs/<code>/<pack>, /insights-lab/game/<code>, …
const GAME_IN_PATH = /^\/(?:match-room|match-video|clip-room|clip-lab|attacking-match-summary|defending-match-summary|tactical-lab|insights-packs|insights-lab\/game|manage-insights\/game)\/([^/]+)/

/** Where an event happens, from the URL: `surface` (a stable name per
 *  area) and `game_code` when the route is about one game. */
export function contextFor(pathname: string, search = ''): { surface: string; game_code: string | null } {
  let surface = SURFACES.find(([re]) => re.test(pathname))?.[1]
    ?? (pathname.split('/')[1] || 'home')
  // /match-video/<code>?view=video is the bare viewer, not the match page
  if (surface === 'match_video' && new URLSearchParams(search).get('view') === 'video') {
    surface = 'video_viewer'
  }
  const m = GAME_IN_PATH.exec(pathname)
  let game: string | null = null
  if (m?.[1]) {
    try { game = decodeURIComponent(m[1]) } catch { game = m[1] }
  }
  return { surface, game_code: game }
}

/** Register the current route's context as super properties, clearing any
 *  that no longer apply. Called on every page view. */
function registerContext(): void {
  const { surface, game_code } = contextFor(window.location.pathname, window.location.search)
  mixpanel.register({ surface, ...(game_code ? { game_code } : {}) })
  if (!game_code) {
    mixpanel.unregister('game_code')
    mixpanel.unregister('game_id')
  }
}

/** A page that learns its game's numeric id (or a game it was not given
 *  in the URL) adds it to every later event on that page. */
export function setGameContext(game: { game_code?: string | null; game_id?: number | null }): void {
  if (!ready) return
  try {
    const props: Record<string, unknown> = {}
    if (game.game_code) props.game_code = game.game_code
    if (game.game_id != null) props.game_id = game.game_id
    if (Object.keys(props).length) mixpanel.register(props)
  } catch (e) {
    console.error('[Mixpanel] context error:', e)
  }
}

/** Safe fire-and-forget — analytics must never break the app.
 *
 *  Every event carries base.html's common properties (timestamp, page,
 *  page_title, referrer). Identity does NOT need restamping here: the old
 *  template put user_id/user_email on every event by hand because two
 *  independent script blocks shared no state; this module registers them
 *  as Mixpanel super-properties at identify, which ride every event. */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  if (!ready || suppressed) return
  try {
    const full = {
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer,
      ...props,
    }
    mixpanel.track(name, full)
    // base.html echoed events on localhost; keep that debugging affordance.
    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      console.log('[Mixpanel] Event tracked:', name, full)
    }
  } catch (e) {
    console.error('[Mixpanel] track error:', e)
  }
}

/** Same event name and props as the templates' pageview — header.html's
 *  page/referrer/url plus base.html's device and UTM context — for
 *  dashboard continuity. */
export function trackPageView(): void {
  if (ready && !suppressed) {
    try { registerContext() } catch (e) { console.error('[Mixpanel] context error:', e) }
  }
  const q = new URLSearchParams(window.location.search)
  trackEvent('Page View', {
    page: window.location.pathname,
    referrer: document.referrer,
    url: window.location.href,
    screen_height: window.screen.height,
    screen_width: window.screen.width,
    viewport_height: window.innerHeight,
    viewport_width: window.innerWidth,
    user_agent: navigator.userAgent,
    // Deprecated API, kept deliberately: base.html sent it and dashboard
    // filters may key on it. Browsers still answer.
    platform: navigator.platform,
    language: navigator.language,
    utm_source: q.get('utm_source'),
    utm_medium: q.get('utm_medium'),
    utm_campaign: q.get('utm_campaign'),
  })
}

/** base.html's global error tracking, ported: uncaught errors become
 *  'JavaScript Error' events. unhandledrejection is new — promise-heavy
 *  React fails that way far more often than via window.onerror. */
export function installErrorTracking(): void {
  window.addEventListener('error', (event) => {
    trackEvent('JavaScript Error', {
      error_message: event.message,
      error_stack: event.error instanceof Error && event.error.stack
        ? event.error.stack : 'No stack trace',
      error_url: window.location.href,
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const r: unknown = event.reason
    trackEvent('JavaScript Error', {
      error_message: r instanceof Error ? r.message : String(r),
      error_stack: r instanceof Error && r.stack ? r.stack : 'No stack trace',
      error_url: window.location.href,
      unhandled_rejection: true,
    })
  })
}

/** base.html's click/submit auto-capture, re-keyed for the React DOM.
 *
 *  The old selectors were Bootstrap classes (.nav-link, .btn,
 *  .dropdown-item) that no longer exist here — a verbatim port would
 *  match nothing. Same event names and props, new matching:
 *  - Navigation Click: any anchor inside a <nav>
 *  - Button Click: any <button> (closest(), because in React the click
 *    target is usually a <span> or icon INSIDE the button — the old
 *    target.matches('button') silently missed those)
 *  - Form Submit: any <form>
 */
export function installAutoCapture(): void {
  document.addEventListener('click', (event) => {
    const t = event.target
    if (!(t instanceof Element)) return
    const link = t.closest('nav a')
    if (link instanceof HTMLAnchorElement) {
      trackEvent('Navigation Click', {
        link_text: (link.textContent ?? '').trim(),
        link_href: link.href,
        link_class: link.className,
      })
      return
    }
    const button = t.closest('button')
    if (button) {
      trackEvent('Button Click', {
        button_text: buttonName(button),
        button_class: button.className,
        button_id: button.id,
      })
    }
  })
  document.addEventListener('submit', (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    trackEvent('Form Submit', {
      form_id: form.id,
      form_action: form.action,
      form_method: form.method,
    })
  })
}

/** What to call a clicked button: an explicit data-track name, else its
 *  visible text, else its aria-label or title. Icon and number buttons
 *  ("⋯", "×", "5") recorded as blank or as a bare digit before
 *  2026-10-21; giving them an aria-label now names them here too. */
export function buttonName(button: Element): string {
  const explicit = button.getAttribute('data-track')?.trim()
  if (explicit) return explicit
  const text = (button.textContent ?? '').trim()
  const label = (button.getAttribute('aria-label') ?? button.getAttribute('title') ?? '').trim()
  // A label beats text that is only a glyph or a number ("⋯", "×", "30").
  if (label && (!text || !/[A-Za-z]{2}/.test(text))) return label
  return text || label
}

/** base.html's Page Unload engagement metric, off pagehide instead of
 *  beforeunload + the removed performance.timing: beforeunload does not
 *  fire on mobile tab kills or bfcache navigations, pagehide does.
 *  Delivery relies on the SDK's persisted batch queue — an event tracked
 *  during teardown is written to localStorage and ships on the next
 *  load. */
export function installEngagementTracking(): void {
  window.addEventListener('pagehide', () => {
    trackEvent('Page Unload', {
      time_on_page_seconds: Math.round(performance.now() / 1000),
    })
  })
}

/** Test seam. */
export function _resetForTests(): void {
  ready = false
  suppressed = false
}
